/**
 * Global Circuit Breaker for Supabase database queries.
 *
 * Prevents retry storms from saturating the PostgreSQL connection pool.
 * When multiple consecutive failures are detected, ALL queries are blocked
 * for a cooldown period, giving the pool time to recover.
 *
 * States:
 *   CLOSED  → normal operation, queries allowed
 *   OPEN    → failures exceeded threshold, queries BLOCKED for cooldown
 *   HALF_OPEN → cooldown expired, ONE probe query allowed to test recovery
 *
 * Modo degradado: quando OPEN, useWorkspace/useUnread etc. já não disparam
 * novas queries; preservam login, inbox e thread. Jobs pesados (follow-up,
 * process-followups) são limitados por workspace no backend (claims/TTL).
 */

type CircuitState = "CLOSED" | "OPEN" | "HALF_OPEN";
type Listener = (state: CircuitState) => void;

const FAILURE_THRESHOLD = 5;
const COOLDOWN_MS = 2 * 60 * 1000; // 2 minutes
const HALF_OPEN_TIMEOUT_MS = 10_000; // 10s to wait for probe result

let state: CircuitState = "CLOSED";
let consecutiveFailures = 0;
let openedAt = 0;
let halfOpenProbeInFlight = false;
const listeners = new Set<Listener>();

function notify() {
  listeners.forEach((fn) => fn(state));
}

function transitionTo(newState: CircuitState) {
  if (state === newState) return;
  console.log(`[CircuitBreaker] ${state} → ${newState} (failures: ${consecutiveFailures})`);
  state = newState;
  if (newState === "OPEN") {
    openedAt = Date.now();
  }
  notify();
}

/**
 * Check if the circuit allows a query to proceed.
 * Returns true if the query should be BLOCKED.
 */
export function isCircuitOpen(): boolean {
  if (state === "CLOSED") return false;

  if (state === "OPEN") {
    // Check if cooldown has elapsed
    if (Date.now() - openedAt >= COOLDOWN_MS) {
      transitionTo("HALF_OPEN");
      return false; // Allow one probe
    }
    return true; // Still in cooldown
  }

  // HALF_OPEN: allow one probe query
  if (state === "HALF_OPEN") {
    if (halfOpenProbeInFlight) return true; // Only one probe at a time
    halfOpenProbeInFlight = true;
    // Auto-reset probe flag after timeout (in case caller forgets to report)
    setTimeout(() => {
      halfOpenProbeInFlight = false;
    }, HALF_OPEN_TIMEOUT_MS);
    return false;
  }

  return false;
}

/**
 * Report a successful database operation.
 * Resets the circuit to CLOSED.
 */
export function recordSuccess() {
  consecutiveFailures = 0;
  halfOpenProbeInFlight = false;
  if (state !== "CLOSED") {
    transitionTo("CLOSED");
  }
}

/**
 * Report a failed database operation.
 * After FAILURE_THRESHOLD consecutive failures, opens the circuit.
 */
export function recordFailure() {
  consecutiveFailures++;
  halfOpenProbeInFlight = false;

  if (consecutiveFailures >= FAILURE_THRESHOLD && state !== "OPEN") {
    transitionTo("OPEN");
  }
}

/**
 * Get current circuit state.
 */
export function getCircuitState(): CircuitState {
  // Re-evaluate OPEN → HALF_OPEN transition
  if (state === "OPEN" && Date.now() - openedAt >= COOLDOWN_MS) {
    transitionTo("HALF_OPEN");
  }
  return state;
}

/**
 * Subscribe to state changes.
 */
export function onCircuitChange(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * Helper: wraps a Supabase query result check.
 * Call after every supabase query to auto-track success/failure.
 */
export function trackQueryResult(error: any): void {
  if (error) {
    const msg = error?.message || error?.details || "";
    const isTransient =
      msg.includes("Failed to fetch") ||
      msg.includes("NetworkError") ||
      msg.includes("timeout") ||
      msg.includes("schema cache") ||
      msg.includes("PGRST002") ||
      msg.includes("connection") ||
      msg.includes("503") || msg.includes("504") || msg.includes("502") ||
      msg.includes("57014") ||
      msg.includes("upstream request timeout") ||
      error?.code === "PGRST002" ||
      error?.code === "57014";

    if (isTransient) {
      recordFailure();
    }
  } else {
    recordSuccess();
  }
}
